import React, { useState } from "react";
import "../css/contact.css";
import Navbar from "../components/navbar";
import { Helmet } from "react-helmet-async";
import Footer from "../components/footer";

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "general",
    message: "",
  });

  // State to track submission status (idle, submitting, success, error)
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "submitting", message: "Sending your message..." });

    try {
      // Submitting to Web3Forms API
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: "2453e52f-1d1b-4c7d-bed0-f8bf6c624ce4",
          // Web3Forms typically looks for a 'name' field, so we combine first and last name
          name: `${formData.firstName} ${formData.lastName}`,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setStatus({
          state: "success",
          message: "Message sent successfully! We'll be in touch.",
        });
        // Reset form after successful submission
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          subject: "general",
          message: "",
        });
      } else {
        setStatus({
          state: "error",
          message: result.message || "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      setStatus({
        state: "error",
        message: "Failed to connect to the server.",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Resumify | Contact Us</title>
        <meta
          name="description"
          content="Get in touch with the Resumify team. Reach out for technical support, general inquiries, or CV suggestions. We're here to help."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://resumify.co.uk/contact" />
      </Helmet>
      <Navbar />
      <div className="contact-page">
        {/* Hero Section */}
        <section className="contact-hero">
          <h1>Contact Us</h1>
          <p>
            We'd love to hear from you. Reach out directly or fill out the form
            below.
          </p>
        </section>

        {/* Main Content Layout */}
        <section className="contact-content">
          <div className="contact-card">
            {/* Left Column: Contact Information */}
            <div className="contact-info-column">
              <div className="contact-method-card">
                <div className="method-icon">🛠️</div>
                <div>
                  <h3>Technical Support</h3>
                  <p>
                    Experiencing issues with our platform? Our support team is
                    ready to assist you.
                  </p>
                </div>
              </div>

              <div className="contact-method-card">
                <div className="method-icon">💬</div>
                <div>
                  <h3>General Inquiry</h3>
                  <p>
                    Have a general question or partnership proposal? Let us know
                    and we'll reply within 24 hours.
                  </p>
                </div>
              </div>

              <div className="contact-method-card">
                <div className="method-icon">📄</div>
                <div>
                  <h3>CV Suggestions</h3>
                  <p>
                    If you want any changes regarding the CV, give us some
                    suggestions and we'll review them.
                  </p>
                </div>
              </div>

              <div className="contact-email-card">
                <span>Prefer to email directly?</span>
                <a href="mailto:admin@resumify.co.uk" className="method-link">
                  admin@resumify.co.uk
                </a>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="contact-form-column">
              <h2>Send a message</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName" className="form-label">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      className="form-input"
                      placeholder="Jane"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName" className="form-label">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      className="form-input"
                      placeholder="Doe"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="form-input"
                    placeholder="jane@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject" className="form-label">
                    Subject
                  </label>
                  <select
                    id="subject"
                    className="form-input"
                    value={formData.subject}
                    onChange={handleChange}
                  >
                    <option value="general">General Inquiry</option>
                    <option value="support">Technical Support</option>
                    <option value="cv">CV Suggestions</option>
                    <option value="billing">Billing Question</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    Message
                  </label>
                  <textarea
                    id="message"
                    className="form-textarea"
                    rows="5"
                    placeholder="How can we help you?"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="contact-button w-full"
                  disabled={status.state === "submitting"}
                  style={{ opacity: status.state === "submitting" ? 0.7 : 1 }}
                >
                  {status.state === "submitting"
                    ? "Sending..."
                    : "Send Message"}
                </button>

                {/* Status Message Display */}
                {status.message && (
                  <div
                    style={{
                      marginTop: "15px",
                      padding: "10px",
                      borderRadius: "5px",
                      textAlign: "center",
                      backgroundColor:
                        status.state === "success"
                          ? "#d4edda"
                          : status.state === "error"
                            ? "#f8d7da"
                            : "transparent",
                      color:
                        status.state === "success"
                          ? "#155724"
                          : status.state === "error"
                            ? "#721c24"
                            : "#333",
                    }}
                  >
                    {status.message}
                  </div>
                )}

                <span className="privacy-note">
                  By submitting, you agree to our Privacy Policy.
                </span>
              </form>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Contact;
